import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import LoginLink from "@via/components/login-link.tsx";
import FoundryConnectLogo from "@via/components/foundry-connect-logo.tsx";
import { useSelfSignInvitation } from "@via/pages/organization/organization.api.tsx";
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_RULES,
  maxLengthValidation,
  requiredValidation,
} from "@via/utils/form-validators.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import { SelfSignInvitationRequestBody } from "common";
import React, { useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

const passwordCriteriaList = [
  {
    id: "length",
    text: `${PASSWORD_MIN_LENGTH} characters minimum`,
    test: (v: string) => v.length >= PASSWORD_MIN_LENGTH,
  },
  ...PASSWORD_RULES.map(rule => ({
    id: rule.key,
    text: rule.message,
    test: (v: string) => rule.pattern.test(v),
  })),
];

interface UrlInviteAccountInfoFormProps {
  slug: string;
  email: string;
  onSuccess: () => void;
}

const UrlInviteAccountInfoForm: React.FC<UrlInviteAccountInfoFormProps> = ({
  slug,
  email,
  onSuccess,
}) => {
  const {
    mutate: selfSignInvitation,
    isPending: isSubmitLoading,
    isSuccess: isSubmitSuccess,
  } = useSelfSignInvitation();

  useEffect(() => {
    if (isSubmitSuccess) {
      onSuccess();
    }
  }, [isSubmitSuccess, onSuccess]);

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { isValid },
  } = useForm<SelfSignInvitationRequestBody>({
    mode: "onChange",
    defaultValues: { marketingConsent: false },
  });

  const passwordValue = watch("password", "");

  const passwordCriteriaMet = useMemo(() => {
    const criteria: { [key: string]: boolean } = {};
    passwordCriteriaList.forEach(c => {
      criteria[c.id] = c.test(passwordValue);
    });
    return criteria;
  }, [passwordValue]);

  const isPasswordStrong = Object.values(passwordCriteriaMet).every(Boolean);

  const onSubmit: SubmitHandler<SelfSignInvitationRequestBody> = data => {
    selfSignInvitation({
      slug,
      email,
      username: data.username,
      firstName: data.firstName,
      lastName: data.lastName,
      password: data.password,
      marketingConsent: data.marketingConsent ?? false,
    });
  };

  return (
    <div className="w-full max-w-md p-8 pt-1 space-y-8">
      <FoundryConnectLogo />
      <h2 className="text-3xl font-bold text-center text-gray-900">
        Create your account
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <FormInputComponent
          control={control}
          name="username"
          label="Username"
          placeholder="Username"
          validations={{
            ...requiredValidation("Username"),
            ...maxLengthValidation(
              40,
              "Username must be less than 40 characters"
            ),
          }}
          className="py-3"
        />

        <FormInputComponent
          control={control}
          name="firstName"
          label="First Name"
          placeholder="First Name"
          validations={{
            ...requiredValidation("First Name"),
            ...maxLengthValidation(
              40,
              "First name must be less than 40 characters"
            ),
          }}
          className="py-3"
        />

        <FormInputComponent
          control={control}
          name="lastName"
          label="Last Name"
          placeholder="Last Name"
          validations={{
            ...requiredValidation("Last Name"),
            ...maxLengthValidation(
              40,
              "Last name must be less than 40 characters"
            ),
          }}
          className="py-3"
        />

        <div>
          <FormInputComponent
            control={control}
            name="password"
            label="Password"
            type="password"
            validations={{
              required: "Password is required",
              validate: (value: string) => {
                const allMet = passwordCriteriaList.every(c => c.test(value));
                return allMet || "Password does not meet all criteria";
              },
            }}
            className="py-3"
          />
          {passwordValue && (
            <p
              className={classNames(
                isPasswordStrong ? "text-green-600" : "text-red-600",
                "mt-2 text-sm"
              )}>
              Password is {isPasswordStrong ? "strong" : "weak"}
            </p>
          )}
        </div>

        <ul className="space-y-1">
          {passwordCriteriaList.map(criterion => {
            const isMet = passwordCriteriaMet[criterion.id];
            return (
              <li
                key={criterion.id}
                className={classNames(
                  isMet ? "text-green-600" : "text-gray-500",
                  "flex items-center text-sm"
                )}>
                {isMet ? (
                  <CheckCircleIcon className="w-4 h-4 mr-2" />
                ) : (
                  <XCircleIcon className="w-4 h-4 mr-2 text-gray-400" />
                )}
                {criterion.text}
              </li>
            );
          })}
        </ul>

        <div className="flex items-center">
          <input
            id="marketingConsent-invitation"
            type="checkbox"
            {...register("marketingConsent")}
            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
          />
          <label
            htmlFor="marketingConsent"
            className="block ml-2 text-sm text-gray-900">
            I agree to receive updates, news, and offers from Foundry Connect.
          </label>
        </div>

        <button
          type="submit"
          disabled={!isValid || isSubmitLoading}
          className="w-full px-4 py-3 font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-300 disabled:cursor-not-allowed">
          <Spinner loading={isSubmitLoading}>Submit</Spinner>
        </button>
      </form>

      <LoginLink />
    </div>
  );
};

export default UrlInviteAccountInfoForm;
